import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import {
  Coins, TrendingUp, Users, ArrowDownCircle, ArrowUpCircle,
  Sparkles, Settings2, BarChart3, ShoppingCart, MapPin, Gift, ArrowRight
} from 'lucide-react'

interface Stats {
  enabled: boolean
  cashback_rate: number
  wavecoin_value: number
  max_discount_pct: number
  min_balance_to_use: number
  wavecoin_expiry_days: number
  checkin_reward_coins: number
  first_purchase_bonus_coins: number
  total_in_circulation: number
  total_in_circulation_reais: number
  total_credited_all_time: number
  total_debited_all_time: number
  total_expired_all_time: number
  credited_this_month: number
  debited_this_month: number
  expired_this_month: number
  discount_granted: number
  wallets_count: number
  total_students: number
}

interface TopStudent {
  id: number
  name: string
  email: string
  balance: number
  balance_reais: number
}

interface Transaction {
  id: number
  user_name: string
  transaction_type: string
  amount: number
  balance_after: number
  description: string
  created_at: string
}

interface WaveCoinsProps {
  stats: Stats
  top_students: TopStudent[]
  recent_transactions: Transaction[]
}

const transactionTypeLabels: Record<string, { label: string; color: string }> = {
  credit: { label: 'Credito', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  debit: { label: 'Debito', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  expiry: { label: 'Expirado', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' },
  reversal: { label: 'Estorno', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
}

export default function WaveCoinsIndex({ stats, top_students, recent_transactions }: WaveCoinsProps) {
  const [enabled, setEnabled] = useState(stats.enabled)
  const [cashbackRate, setCashbackRate] = useState(stats.cashback_rate)
  const [maxDiscountPct, setMaxDiscountPct] = useState(stats.max_discount_pct)
  const [minBalanceToUse, setMinBalanceToUse] = useState(stats.min_balance_to_use)
  const [wavecoinExpiryDays, setWavecoinExpiryDays] = useState(stats.wavecoin_expiry_days)
  const [checkinRewardCoins, setCheckinRewardCoins] = useState(stats.checkin_reward_coins)
  const [firstPurchaseBonus, setFirstPurchaseBonus] = useState(stats.first_purchase_bonus_coins)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const coinValue = stats.wavecoin_value / 100 // valor em reais de 1 coin

  const hasChanges = enabled !== stats.enabled ||
    cashbackRate !== stats.cashback_rate ||
    maxDiscountPct !== stats.max_discount_pct ||
    minBalanceToUse !== stats.min_balance_to_use ||
    wavecoinExpiryDays !== stats.wavecoin_expiry_days ||
    checkinRewardCoins !== stats.checkin_reward_coins ||
    firstPurchaseBonus !== stats.first_purchase_bonus_coins

  const handleSaveConfig = () => {
    setIsSubmitting(true)
    router.patch('/settings', {
      school: {
        wavecoin_enabled: enabled,
        cashback_rate: cashbackRate,
        max_discount_pct: maxDiscountPct,
        min_balance_to_use: minBalanceToUse,
        wavecoin_expiry_days: wavecoinExpiryDays,
        checkin_reward_coins: checkinRewardCoins,
        first_purchase_bonus_coins: firstPurchaseBonus,
      }
    }, {
      onSuccess: () => {
        toast.success('Configuracoes salvas!')
        setIsSubmitting(false)
      },
      onError: () => {
        toast.error('Erro ao salvar')
        setIsSubmitting(false)
      },
    })
  }

  const handleCancel = () => {
    setEnabled(stats.enabled)
    setCashbackRate(stats.cashback_rate)
    setMaxDiscountPct(stats.max_discount_pct)
    setMinBalanceToUse(stats.min_balance_to_use)
    setWavecoinExpiryDays(stats.wavecoin_expiry_days)
    setCheckinRewardCoins(stats.checkin_reward_coins)
    setFirstPurchaseBonus(stats.first_purchase_bonus_coins)
  }

  // Simulação com valores configurados
  const simPackagePrice = 500
  const simCashbackCoins = Math.round(simPackagePrice * cashbackRate / 100)
  const simCheckinMonth = checkinRewardCoins * 8 // 2 aulas/semana * 4 semanas
  const simFirstMonth = simCashbackCoins + firstPurchaseBonus + simCheckinMonth
  const simFirstMonthReais = simFirstMonth * coinValue
  const simMaxDiscount = simPackagePrice * maxDiscountPct / 100

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* ── BANNER ── */}
        <div className={`relative overflow-hidden rounded-xl shadow-lg transition-all duration-300`}>
          <div className={`absolute inset-0 transition-all duration-300 ${enabled ? 'bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700' : 'bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600'}`} />
          <div className="absolute inset-0 opacity-[0.07]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827zM5.373 0l-.83.828L5.96 2.243 8.2 0H5.374zM48.97 0l3.657 3.657-1.414 1.414L46.143 0h2.828zM11.03 0L7.372 3.657 8.787 5.07 13.857 0H11.03zm32.284 0L49.8 6.485 48.384 7.9l-7.9-7.9h2.83zM16.686 0L10.2 6.485 11.616 7.9l7.9-7.9h-2.83z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
          <div className="relative p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className={`h-5 w-5 ${enabled ? 'text-yellow-300' : 'text-gray-300'}`} />
                <div>
                  <h3 className="text-lg font-bold text-white">Fidelize seus alunos com cashback</h3>
                  <p className="text-sm text-white/70">Cada compra e check-in gera coins que viram desconto na proxima compra</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-white/80 hidden sm:block">
                  {enabled ? 'Ativo' : 'Desativado'}
                </span>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  className="data-[state=checked]:bg-white/90 data-[state=unchecked]:bg-white/20 [&>span]:data-[state=checked]:bg-blue-600 [&>span]:data-[state=unchecked]:bg-white/70"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        {enabled && (
          <Tabs defaultValue="config">
            <TabsList>
              <TabsTrigger value="config">
                <Settings2 className="h-4 w-4 mr-1.5" />
                Configuracoes
              </TabsTrigger>
              <TabsTrigger value="dashboard">
                <BarChart3 className="h-4 w-4 mr-1.5" />
                Dashboard
              </TabsTrigger>
            </TabsList>

            {/* ── TAB CONFIGURACOES ── */}
            <TabsContent value="config" className="space-y-6 mt-6">

              {/* Formulario limpo sem cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Coluna esquerda: Regras */}
                <div className="space-y-8">
                  {/* Acumulo */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4" />
                      Regras de Acumulo
                    </h3>
                    <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-border">
                      <div className="space-y-1.5">
                        <Label htmlFor="cashback_rate" className="text-sm">Taxa de Cashback</Label>
                        <div className="flex items-center gap-2">
                          <Input id="cashback_rate" type="number" min="0" max="100" step="0.5" value={cashbackRate} onChange={(e) => setCashbackRate(parseFloat(e.target.value) || 0)} className="w-20" />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="checkin_coins" className="text-sm">Coins por Check-in</Label>
                        <div className="flex items-center gap-2">
                          <Input id="checkin_coins" type="number" min="0" max="100" step="1" value={checkinRewardCoins} onChange={(e) => setCheckinRewardCoins(parseInt(e.target.value) || 0)} className="w-20" />
                          <span className="text-sm text-muted-foreground">coins</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="first_purchase" className="text-sm">Bonus 1a Compra</Label>
                        <div className="flex items-center gap-2">
                          <Input id="first_purchase" type="number" min="0" max="500" step="5" value={firstPurchaseBonus} onChange={(e) => setFirstPurchaseBonus(parseInt(e.target.value) || 0)} className="w-20" />
                          <span className="text-sm text-muted-foreground">coins</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="expiry_days" className="text-sm">Validade</Label>
                        <div className="flex items-center gap-2">
                          <Input id="expiry_days" type="number" min="30" step="30" value={wavecoinExpiryDays} onChange={(e) => setWavecoinExpiryDays(parseInt(e.target.value) || 365)} className="w-20" />
                          <span className="text-sm text-muted-foreground">dias</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resgate */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Gift className="h-4 w-4" />
                      Regras de Resgate
                    </h3>
                    <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-border">
                      <div className="space-y-1.5">
                        <Label htmlFor="max_discount" className="text-sm">Desconto Maximo</Label>
                        <div className="flex items-center gap-2">
                          <Input id="max_discount" type="number" min="0" max="100" step="1" value={maxDiscountPct} onChange={(e) => setMaxDiscountPct(parseFloat(e.target.value) || 0)} className="w-20" />
                          <span className="text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="min_balance" className="text-sm">Saldo Minimo</Label>
                        <div className="flex items-center gap-2">
                          <Input id="min_balance" type="number" min="0" step="10" value={minBalanceToUse} onChange={(e) => setMinBalanceToUse(parseInt(e.target.value) || 0)} className="w-20" />
                          <span className="text-sm text-muted-foreground">coins</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Coluna direita: Simulacao */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Simulacao com suas configuracoes
                  </h3>

                  <div className="rounded-xl border bg-muted/30 p-5 space-y-5">
                    <p className="text-xs text-muted-foreground">
                      Exemplo: aluno compra pacote de <span className="font-semibold text-foreground">R$ {simPackagePrice}</span> e faz <span className="font-semibold text-foreground">2 aulas/semana</span>
                    </p>

                    {/* Acumulo no 1o mes */}
                    <div className="space-y-2.5">
                      <p className="text-xs font-semibold text-foreground">Primeiro mes do aluno:</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <ShoppingCart className="h-3.5 w-3.5" />
                            Cashback da compra ({cashbackRate}%)
                          </span>
                          <span className="font-medium text-green-600">+{simCashbackCoins} coins</span>
                        </div>
                        {firstPurchaseBonus > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <Gift className="h-3.5 w-3.5" />
                              Bonus primeira compra
                            </span>
                            <span className="font-medium text-green-600">+{firstPurchaseBonus} coins</span>
                          </div>
                        )}
                        {checkinRewardCoins > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5" />
                              8 check-ins ({checkinRewardCoins}/aula)
                            </span>
                            <span className="font-medium text-green-600">+{simCheckinMonth} coins</span>
                          </div>
                        )}
                        <div className="border-t pt-1.5 mt-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-semibold">Total no 1o mes</span>
                            <span className="font-bold text-foreground">{simFirstMonth} coins = R$ {simFirstMonthReais.toFixed(0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quando consegue o desconto */}
                    <div className="rounded-lg bg-background border p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="h-4 w-4 text-primary" />
                        <p className="text-sm font-semibold">Na proxima compra</p>
                      </div>
                      {simFirstMonth >= minBalanceToUse ? (
                        <p className="text-sm text-muted-foreground">
                          Com <span className="font-semibold text-foreground">{simFirstMonth} coins</span>, o aluno pode usar ate{' '}
                          <span className="font-semibold text-green-600">R$ {Math.min(simFirstMonthReais, simMaxDiscount).toFixed(0)} de desconto</span> (limite de {maxDiscountPct}% = R$ {simMaxDiscount.toFixed(0)}).
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Ainda precisa acumular mais <span className="font-semibold text-foreground">{minBalanceToUse - simFirstMonth} coins</span> para atingir o minimo de {minBalanceToUse}.
                          {checkinRewardCoins > 0 && ` Mais ${Math.ceil((minBalanceToUse - simFirstMonth) / checkinRewardCoins)} check-ins.`}
                        </p>
                      )}
                    </div>

                    {/* Valor por coin */}
                    <p className="text-xs text-muted-foreground text-center">
                      1 WaveCoin = R$ {coinValue.toFixed(2)} de desconto
                    </p>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={handleCancel} disabled={isSubmitting || !hasChanges}>
                      Cancelar
                    </Button>
                    <Button onClick={handleSaveConfig} disabled={isSubmitting || !hasChanges}>
                      {isSubmitting ? 'Salvando...' : 'Salvar Configuracoes'}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── TAB DASHBOARD ── */}
            <TabsContent value="dashboard" className="space-y-6 mt-6">
              {!stats.enabled ? (
                <div className="text-center py-12 space-y-2">
                  <Coins className="h-8 w-8 text-muted-foreground mx-auto" />
                  <p className="text-sm font-medium">Ative o WaveCoins para visualizar as metricas</p>
                  <p className="text-xs text-muted-foreground">Salve as configuracoes com WaveCoins ativo para comecar a acompanhar os dados.</p>
                </div>
              ) : (
                <>
                  {/* Metricas */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-lg border p-4 space-y-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Coins className="h-3.5 w-3.5" /> Em circulacao</p>
                      <p className="text-2xl font-bold">{stats.total_in_circulation.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">R$ {stats.total_in_circulation_reais.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border p-4 space-y-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Alunos com saldo</p>
                      <p className="text-2xl font-bold">{stats.wallets_count}</p>
                      <p className="text-xs text-muted-foreground">de {stats.total_students} alunos</p>
                    </div>
                    <div className="rounded-lg border p-4 space-y-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowDownCircle className="h-3.5 w-3.5 text-green-500" /> Creditado este mes</p>
                      <p className="text-2xl font-bold text-green-600">+{stats.credited_this_month.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{stats.debited_this_month.toLocaleString()} usados</p>
                    </div>
                    <div className="rounded-lg border p-4 space-y-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Desconto concedido</p>
                      <p className="text-2xl font-bold">R$ {stats.discount_granted.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{stats.expired_this_month.toLocaleString()} expirados no mes</p>
                    </div>
                  </div>

                  {/* Top Alunos + Transacoes */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Top Alunos
                      </h3>
                      {top_students.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">Nenhum aluno com saldo ainda</p>
                      ) : (
                        <div className="rounded-lg border divide-y">
                          {top_students.map((student, i) => (
                            <div key={student.id} className="flex items-center justify-between px-4 py-3">
                              <div className="flex items-center gap-3">
                                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                  i === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                                  i === 1 ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' :
                                  i === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {i + 1}
                                </span>
                                <div>
                                  <p className="text-sm font-medium">{student.name}</p>
                                  <p className="text-xs text-muted-foreground">{student.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold">{student.balance.toLocaleString()}</p>
                                <p className="text-xs text-muted-foreground">R$ {student.balance_reais.toFixed(2)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Movimentacoes Recentes
                      </h3>
                      {recent_transactions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma transacao ainda</p>
                      ) : (
                        <div className="rounded-lg border divide-y max-h-[450px] overflow-y-auto">
                          {recent_transactions.map((tx) => {
                            const typeInfo = transactionTypeLabels[tx.transaction_type] || { label: tx.transaction_type, color: 'bg-gray-100 text-gray-800' }
                            const isPositive = tx.transaction_type === 'credit' || tx.transaction_type === 'reversal'
                            return (
                              <div key={tx.id} className="flex items-center justify-between px-4 py-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  {isPositive ? (
                                    <ArrowDownCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <ArrowUpCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">{tx.user_name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{tx.description}</p>
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0 ml-3">
                                  <p className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                    {isPositive ? '+' : '-'}{Math.abs(tx.amount).toLocaleString()}
                                  </p>
                                  <Badge variant="outline" className={`text-[10px] ${typeInfo.color}`}>
                                    {typeInfo.label}
                                  </Badge>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  )
}
